import i18n from '../index';

/**
 * Number formatting utilities using the Intl.NumberFormat API.
 * Automatically uses the current i18n locale.
 */

export type NumberStyle = 'decimal' | 'currency' | 'percent';

interface NumberFormatOptions {
  style?: NumberStyle;
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  useGrouping?: boolean;
}

/**
 * Formats a number according to the current locale.
 *
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1234.56) // "1,234.56" (en) or "1 234,56" (fr-CA)
 * formatNumber(1234.56, { style: 'currency', currency: 'USD' }) // "$1,234.56"
 * formatNumber(0.15, { style: 'percent' }) // "15%"
 */
export const formatNumber = (
  value: number,
  options: NumberFormatOptions = {},
): string => {
  const {
    style = 'decimal',
    currency = 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping = true,
  } = options;

  const locale = i18n.language;

  const formatOptions: Intl.NumberFormatOptions = {
    style,
    useGrouping,
    ...(style === 'currency' ? { currency } : {}),
    ...(minimumFractionDigits !== undefined ? { minimumFractionDigits } : {}),
    ...(maximumFractionDigits !== undefined ? { maximumFractionDigits } : {}),
  };

  try {
    return new Intl.NumberFormat(locale, formatOptions).format(value);
  } catch {
    return new Intl.NumberFormat('en', formatOptions).format(value);
  }
};

/**
 * Formats a number as currency according to the current locale.
 *
 * @param value - The number to format
 * @param currency - The currency code (default: 'USD')
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(1234.56) // "$1,234.56" (en) or "1 234,56 $ US" (fr-CA)
 * formatCurrency(1234.56, 'CAD') // "CA$1,234.56" or "1 234,56 $ CA"
 */
export const formatCurrency = (value: number, currency = 'USD'): string =>
  formatNumber(value, { style: 'currency', currency });

/**
 * Formats a number as a percentage according to the current locale.
 *
 * @param value - The number to format (0.15 = 15%)
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted percentage string
 *
 * @example
 * formatPercent(0.15) // "15%"
 * formatPercent(0.1567, 2) // "15.67%"
 */
export const formatPercent = (value: number, decimals = 0): string =>
  formatNumber(value, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

/**
 * Formats a number with a compact notation (e.g., 1K, 1M).
 *
 * @param value - The number to format
 * @returns Formatted compact number string
 *
 * @example
 * formatCompact(1234) // "1.2K"
 * formatCompact(1234567) // "1.2M"
 */
export const formatCompact = (value: number): string => {
  const locale = i18n.language;

  try {
    return new Intl.NumberFormat(locale, {
      notation: 'compact',
      compactDisplay: 'short',
    }).format(value);
  } catch {
    return new Intl.NumberFormat('en', {
      notation: 'compact',
      compactDisplay: 'short',
    }).format(value);
  }
};
